import { Progress } from '@chakra-ui/react';
import { IAccountRespSchema, IQuoteRequestSchema } from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import {
  MBox,
  MButton,
  MCenterModal,
  MSpacer,
  MStack,
  MText,
} from '../../../../../components/Monetize';
import { useCustomFieldsAll } from '../../../../../hooks/useCustomFieldsAll';
import { isQuestionSubmitDisabled } from './guidedQuote.utils';
import { QuestionForm } from './questionForm/QuestionForm';
import {
  useGuidedQuoteProcess,
  UseGuidedQuoteProcessProps,
} from './useGuidedQuoteProcess';

interface NewGuidedQuoteModalProps extends Pick<
  UseGuidedQuoteProcessProps,
  'getInitialQuoteFieldValues' | 'onQuoteCreated' | 'onFinished'
> {
  account: IAccountRespSchema;
  initialQuoteData?: Partial<IQuoteRequestSchema>;
  existingOpportunityId?: Maybe<string>;
  onCancel: () => void;
  onManualQuote: () => void;
  parsedFieldMapping?: Record<string, any>;
}

export const NewGuidedQuoteModal = ({
  account,
  existingOpportunityId,
  initialQuoteData,
  onCancel,
  onManualQuote,
  parsedFieldMapping,
  ...rest
}: NewGuidedQuoteModalProps) => {
  const {
    guidedQuoting,
    // reducer state
    currentQuestion,
    hasNextQuestion,
    hasPreviousQuestion,
    offeringQuestion,
    productQuestion,
    progress,
    // other state
    isLoading,
    context,
    // Actions
    onPreviousQuestion,
    onNextQuestion,
    setContext,
  } = useGuidedQuoteProcess({
    initialQuoteData,
    account,
    parsedFieldMapping,
    existingOpportunityId,
    ...rest,
  });

  const { data: customFieldsData, isLoading: isCustomFieldLoading } =
    useCustomFieldsAll();

  if (
    !currentQuestion ||
    !guidedQuoting ||
    isCustomFieldLoading ||
    !customFieldsData
  ) {
    // Load inside the modal rather than with a page-level MPageLoader, so the
    // flow stays modal-to-modal (account selection -> guided quote) instead
    // of dropping to a bare full-page spinner in between.
    return (
      <MCenterModal
        isOpen
        size="xl"
        onClose={onCancel}
        modalTitle="New Guided Quote"
        closeOnEsc={false}
        closeOnOverlayClick={false}
        isLoading
      />
    );
  }

  return (
    <MCenterModal
      isOpen
      size="xl"
      onClose={onCancel}
      modalTitle="New Guided Quote"
      closeOnEsc={false}
      closeOnOverlayClick={false}
      subTitle={
        <MText fontWeight="normal">
          Creating quote for Account{' '}
          <MBox as="span" fontWeight={500}>
            {account.accountName}
          </MBox>
        </MText>
      }
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" flex={1}>
          {currentQuestion && Object.keys(currentQuestion).length > 0 && (
            <MText>
              Want to create the quote{' '}
              {
                <MButton
                  variant="link"
                  size="sm"
                  fontWeight="normal"
                  _hover={{ bg: 'unset' }}
                  onClick={() => onManualQuote()}
                >
                  manually
                </MButton>
              }
              ?
            </MText>
          )}
          <MSpacer />
          {currentQuestion && Object.keys(currentQuestion).length > 0 ? (
            <MButton
              variant="cancel"
              isDisabled={isLoading || !currentQuestion || !hasPreviousQuestion}
              onClick={onPreviousQuestion}
              minW="auto"
            >
              Back
            </MButton>
          ) : (
            <MButton variant="cancel" onClick={onPreviousQuestion} minW="auto">
              Back
            </MButton>
          )}

          <MButton
            variant="primary"
            onClick={onNextQuestion}
            isDisabled={isQuestionSubmitDisabled(
              context,
              currentQuestion,
              offeringQuestion,
            )}
            isLoading={isLoading}
            minW="auto"
          >
            {hasNextQuestion ? 'Next' : 'Create'}
          </MButton>
        </MStack>
      )}
    >
      <Progress.Root
        colorPalette="tIndigo"
        size="2xs"
        value={progress}
        position="absolute"
        height="4px"
        width="100%"
        top="65px"
        left="0px"
        zIndex={-1}
      >
        <Progress.Track>
          <Progress.Range />
        </Progress.Track>
      </Progress.Root>
      <MBox minH="4rem">
        <QuestionForm
          key={currentQuestion.id}
          question={currentQuestion}
          context={context}
          setContext={setContext}
          productQuestion={productQuestion}
          offeringQuestion={offeringQuestion}
          guidedQuoting={guidedQuoting}
          customFieldsData={customFieldsData}
        />
      </MBox>
    </MCenterModal>
  );
};
